import React, { useState } from 'react';
import PropTypes from 'prop-types';

import './emails.css';
import {
  FIELDS, SETTINGS_FIELDS, findBlock, labelFor, optionsFor, coerceFieldValue,
} from '../../../utils/emailBlockDoc';
import { isContentSafeForRichText } from '../../../utils/tiptapConfig';
import HtmlSourceEditor from './HtmlSourceEditor';
import ImageSourceField from './ImageSourceField';
import { RENDER_HTML_EDITORS } from './htmlEditorSurfaces';

// The properties of whatever is selected — or, when nothing is, the document's
// own settings. One panel, two contents, never both.
//
// There is no form in this file. FIELDS and SETTINGS_FIELDS
// (src/utils/emailBlockDoc.js) list which props each block type has, and the
// tests assert those lists against the serialiser's BLOCK_DEFAULTS key for key
// in both directions. That is what keeps this honest: a control here cannot set
// a prop nothing renders, and a prop the serialiser reads cannot go missing.
//
// Every change is applied immediately. The canvas is never debounced — it is
// the feedback loop — and the proof is debounced by the screen above, not here.
//
// jsx-a11y/label-has-for wants each <label> to BOTH wrap its control and carry
// htmlFor. Three real accessibility defects in this project came from getting
// exactly that wrong, so every label below does both. The colour rows wrap two
// inputs (a swatch and its hex) inside one label, with htmlFor naming the hex
// field and the swatch carrying its own aria-label.

const Field = ({ id, field, value, onChange }) => {
  const current = value === undefined || value === null ? '' : value;
  const change = raw => onChange(field.key, coerceFieldValue(field, raw));

  if (field.kind === 'select') {
    return (
      <div className="field inspector-field">
        <label htmlFor={id}>
          {field.label}
          <select
            id={id}
            value={current}
            onChange={event => change(event.target.value)}
          >
            {optionsFor(field, current).map(option => (
              <option key={String(option.value)} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        {field.hint && <p className="hint">{field.hint}</p>}
      </div>
    );
  }

  if (field.kind === 'color') {
    return (
      <div className="field inspector-field">
        <label htmlFor={id}>
          {field.label}
          <span className="colorline">
            <input
              type="color"
              className="colorswatch"
              aria-label={`${field.label} — colour picker`}
              value={/^#[0-9a-fA-F]{6}$/.test(current) ? current : '#000000'}
              onChange={event => change(event.target.value)}
            />
            <input
              id={id}
              type="text"
              className="textinput mono"
              value={current}
              onChange={event => change(event.target.value)}
            />
          </span>
        </label>
        {field.hint && <p className="hint">{field.hint}</p>}
      </div>
    );
  }

  if (field.kind === 'number') {
    return (
      <div className="field inspector-field">
        <label htmlFor={id}>
          {field.label}
          <span className="numline">
            <input
              id={id}
              type="number"
              className="textinput"
              min={field.min}
              max={field.max}
              value={current}
              onChange={event => change(event.target.value)}
            />
            <span className="unit">{field.unit}</span>
          </span>
        </label>
        {field.hint && <p className="hint">{field.hint}</p>}
      </div>
    );
  }

  return (
    <div className="field inspector-field">
      <label htmlFor={id}>
        {field.label}
        <input
          id={id}
          type="text"
          className="textinput"
          value={current}
          onChange={event => change(event.target.value)}
        />
      </label>
      {field.hint && <p className="hint">{field.hint}</p>}
    </div>
  );
};

Field.propTypes = {
  id: PropTypes.string.isRequired,
  // The shape of one entry in FIELDS / SETTINGS_FIELDS. Spelled out in full
  // rather than as a bare object, so a descriptor that grows a key nobody draws
  // is caught here.
  field: PropTypes.shape({
    key: PropTypes.string.isRequired,
    kind: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    hint: PropTypes.string,
    unit: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
    numeric: PropTypes.bool,
    options: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })),
  }).isRequired,
  // A prop is a string or a number depending on its kind, and mid-edit a number
  // field holds ''. All three are legitimate.
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
};

Field.defaultProps = { value: '' };

/*
The one control that edits a LIST rather than a value: a social row's icons.

Everything else in FIELDS sets a single prop, so <Field> is enough. A social row
is an ordered list of {src, href, alt, width, background, radius}, and the thing
being edited is as much the ORDER and the LENGTH as any one icon — so this draws
one card per icon with add, remove and move controls, and reuses <Field> for the
inputs inside it rather than growing a second set of controls.

Every operation returns a NEW array of NEW objects. Apollo freezes query results
in this admin (`__DEV__` is undefined in the bundles), so a stored document's
icons array is frozen, and splicing it in place would throw in production and
nowhere else.
*/
const IconListField = ({ field, icons, onChange }) => {
  const list = Array.isArray(icons) ? icons : [];

  const replace = next => onChange(field.key, next);
  const patch = (index, key, value) => replace(list.map((icon, i) => (
    i === index ? { ...icon, [key]: value } : { ...icon }
  )));
  const move = (index, delta) => {
    const to = index + delta;
    if (to < 0 || to >= list.length) return;
    const next = list.map(icon => ({ ...icon }));
    const [moved] = next.splice(index, 1);
    next.splice(to, 0, moved);
    replace(next);
  };
  const remove = index => replace(list.filter((icon, i) => i !== index).map(icon => ({ ...icon })));
  const add = () => replace(list.map(icon => ({ ...icon })).concat([{
    src: '', href: '', alt: '', width: 20, background: '', radius: 3,
  }]));

  return (
    <div className="field inspector-field wide">
      <span className="field-label">{field.label}</span>
      {field.hint && <p className="hint">{field.hint}</p>}

      {list.length === 0 && (
        <p className="hint">
          No icons yet. A social row with none in it is sent as nothing at all — not as an
          empty gap.
        </p>
      )}

      {list.map((icon, index) => (
        <div className="iconrow" key={`icon-${index + 1}`}>
          <div className="iconrow-head">
            <span className="locked">{`Icon ${index + 1}`}</span>
            <div className="right">
              <button
                type="button"
                className="tbtn"
                title="Move this icon left"
                disabled={index === 0}
                onClick={() => move(index, -1)}
              >
                ←
              </button>
              <button
                type="button"
                className="tbtn"
                title="Move this icon right"
                disabled={index === list.length - 1}
                onClick={() => move(index, 1)}
              >
                →
              </button>
              <button
                type="button"
                className="tbtn"
                title="Remove this icon"
                onClick={() => remove(index)}
              >
                Remove
              </button>
            </div>
          </div>
          {field.fields.map(entry => (
            <Field
              key={entry.key}
              id={`email-block-icon-${index + 1}-${entry.key}`}
              field={entry}
              value={icon[entry.key]}
              onChange={(key, next) => patch(index, key, next)}
            />
          ))}
        </div>
      ))}

      <div className="mactions">
        <button type="button" className="btn tiny ghost" onClick={add}>Add an icon</button>
      </div>
    </div>
  );
};

IconListField.propTypes = {
  field: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    hint: PropTypes.string,
    fields: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string })).isRequired,
  }).isRequired,
  icons: PropTypes.arrayOf(PropTypes.shape({ src: PropTypes.string })),
  onChange: PropTypes.func.isRequired,
};

IconListField.defaultProps = { icons: [] };

const BlockInspector = ({ doc, selectedId, onChangeBlock, onChangeSettings, onSelect }) => {
  // Which text blocks are being edited as raw HTML. Keyed by block id so that
  // switching selection and coming back does not silently flip the control.
  const [sourceFor, setSourceFor] = useState({});
  const block = findBlock(doc, selectedId);

  if (!block) {
    return (
      <React.Fragment>
        <p className="hint">
          Nothing selected — these settings apply to the whole email. Click a block on the
          canvas to edit it.
        </p>
        <div className="inspector-fields">
          {SETTINGS_FIELDS.map(field => (
            <Field
              key={field.key}
              id={`email-doc-${field.key}`}
              field={field}
              value={doc.settings[field.key]}
              onChange={(key, next) => onChangeSettings({ [key]: next })}
            />
          ))}
        </div>
      </React.Fragment>
    );
  }

  const fields = FIELDS[block.type] || [];
  const change = (key, next) => onChangeBlock(block.id, { [key]: next });
  const asSource = Boolean(sourceFor[block.id]) || !isContentSafeForRichText(block.html || '');

  return (
    <React.Fragment>
      <div className="inspector-head">
        <span className="locked marked">{`${labelFor(block.type)} selected`}</span>
        <button type="button" className="btn tiny ghost" onClick={() => onSelect(null)}>
          Deselect
        </button>
      </div>

      <div className="inspector-fields">
        {fields.map((field) => {
          if (field.kind === 'icons') {
            return (
              <IconListField
                key={field.key}
                field={field}
                icons={block[field.key]}
                onChange={change}
              />
            );
          }

          // The one control that can set TWO props at once: picking an asset
          // sets src, and fills in alt from the asset's description when the
          // block has none. Both have to land as a single change, so it gets
          // the whole patch rather than <Field>'s (key, value).
          if (field.kind === 'image') {
            return (
              <ImageSourceField
                key={field.key}
                id={`email-block-${field.key}`}
                field={field}
                value={block[field.key]}
                alt={block.alt}
                onPatch={patch => onChangeBlock(block.id, patch)}
              />
            );
          }

          if (field.kind !== 'richtext') {
            return (
              <Field
                key={field.key}
                id={`email-block-${field.key}`}
                field={field}
                value={block[field.key]}
                onChange={change}
              />
            );
          }

          // THE RICH EDITOR IS NOT HERE ANY MORE. A text block is written with
          // the caret in the email itself — BlockCanvas mounts RichTextEditor in
          // place of the rendered paragraph when the block is selected. Putting
          // a second Tiptap on the same string in this panel would recreate
          // exactly the thing that got two layouts rejected: two places showing
          // the same copy, and no answer to which one you are meant to type in.
          //
          // AND THE HTML ESCAPE HATCH IS NOT HERE ANY MORE EITHER. It used to be
          // the only way to edit a block holding markup Tiptap would drop, and
          // while that was true of half the corpus it had to stay. It is not
          // true any more: every one of the 204 text blocks in the 79
          // block-authored templates is now rich-text editable, measured, so the
          // hatch has nothing left to rescue and the block is edited in the one
          // place the copy actually is. The control below it is unchanged and
          // comes back with one line — see htmlEditorSurfaces.js.
          //
          // Rich text is not an <input>, so it gets a caption rather than a
          // label — a <label> that wraps a contenteditable points at nothing.
          return (
            <div className="field inspector-field wide" key={field.key}>
              <span className="field-label">{field.label}</span>
              {RENDER_HTML_EDITORS && asSource ? (
                <HtmlSourceEditor
                  id={`email-block-${field.key}-source`}
                  label="Block HTML"
                  value={block.html || ''}
                  onChange={event => change('html', event.target.value)}
                  rows={8}
                />
              ) : (
                <p className="hint">
                  Click this block in the email and type. Bold, italic, headings and lists are on
                  the small bar that appears above it.
                </p>
              )}
              {RENDER_HTML_EDITORS && (
                <div className="inspector-sourcetoggle">
                  <button
                    type="button"
                    className="tbtn"
                    onClick={() => setSourceFor(previous => ({ ...previous, [block.id]: !previous[block.id] }))}
                    disabled={!isContentSafeForRichText(block.html || '')}
                    title={isContentSafeForRichText(block.html || '')
                      ? 'Edit the HTML behind this block instead of typing into the email'
                      : 'This block contains markup the rich editor would drop, so it is edited as HTML'}
                  >
                    {asSource ? 'Back to typing in the email' : 'Edit as HTML'}
                  </button>
                  {!isContentSafeForRichText(block.html || '') && (
                    <span className="hint">
                      This block holds markup the rich editor does not model, so it stays as HTML —
                      the email shows it but will not take the caret.
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {fields.length === 0 && (
          <p className="hint">{`No editable properties for a "${block.type}" block.`}</p>
        )}
      </div>
    </React.Fragment>
  );
};

BlockInspector.propTypes = {
  doc: PropTypes.shape({
    settings: PropTypes.shape({}).isRequired,
    blocks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  }).isRequired,
  selectedId: PropTypes.string,
  onChangeBlock: PropTypes.func.isRequired,
  onChangeSettings: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

BlockInspector.defaultProps = {
  selectedId: null,
};

export default BlockInspector;
